import { styled } from "goober";
import {
  extractChatInvestigation,
  getInvestigationContent,
} from "../../../shared/chatInvestigation";
import {
  attachedSkillNames,
  stripAttachedSkillsBlock,
} from "../../../shared/skills/attachedSkills";
import {
  attachedConnectorNames,
  stripAttachedConnectorsBlock,
} from "../../../shared/connectors/attachedConnectors";
import {
  attachedStepNames,
  stripAttachedStepsBlock,
} from "../../../shared/steps/attachedSteps";
import { useMemo } from "react";
import { Icon } from "../Icon";
import { ConnectorLogo } from "../ConnectorLogo";
import { MetaSep } from "../MetaSeparator";
import { Text } from "../Text";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "../../../shared/queries";
import { connectorPillIdentifier } from "./connectorPillIdentifier";

export const CodeContextLabel = styled("span")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px 0 6px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  width: fit-content;
  max-width: 200px;
`;

export const AccentBar = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  flex-shrink: 0;

  &::after {
    content: "";
    width: 4px;
    height: 10px;
    border-radius: 2px;
    background: currentColor;
  }
`;

const VoiceLabel = styled("span")`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 12px;
  border-radius: 38px;
  color: var(--text-secondary);
  width: fit-content;
  align-self: flex-end;
`;

type ParsedCodeContext = {
  file: string;
  lines: string;
  color: string;
};

const codeContextPattern =
  /<code-context file="([^"]*)" lines="([^"]*)" color="([^"]*)">\n[\s\S]*?\n<\/code-context>/g;

function parseCodeContext(text: string): {
  snippets: ParsedCodeContext[];
  remainingText: string;
} {
  const snippets: ParsedCodeContext[] = [];
  let remaining = text;
  for (const match of text.matchAll(codeContextPattern)) {
    snippets.push({ file: match[1]!, lines: match[2]!, color: match[3]! });
    remaining = remaining.replace(match[0], "");
  }
  return { snippets, remainingText: remaining.trim() };
}

type ParsedElementContext = {
  step: string;
  element: string;
  color: string;
  index: string;
};

const elementContextPattern =
  /<element-context step="([^"]*)"(?: source="[^"]*")? element="([^"]*)" color="([^"]*)" index="([^"]*)">\n[\s\S]*?\n<\/element-context>/g;

function parseElementContext(text: string): {
  snippets: ParsedElementContext[];
  remainingText: string;
} {
  const snippets: ParsedElementContext[] = [];
  let remaining = text;
  for (const match of text.matchAll(elementContextPattern)) {
    snippets.push({
      step: match[1]!,
      element: match[2]!,
      color: match[3]!,
      index: match[4]!,
    });
    remaining = remaining.replace(match[0], "");
  }
  return { snippets, remainingText: remaining.trim() };
}

function parseAttachedSkills(text: string): {
  skills: string[];
  remainingText: string;
} {
  return {
    skills: attachedSkillNames(text),
    remainingText: stripAttachedSkillsBlock(text),
  };
}

function parseAttachedSteps(text: string): {
  steps: string[];
  remainingText: string;
} {
  return {
    steps: attachedStepNames(text),
    remainingText: stripAttachedStepsBlock(text),
  };
}

function parseAttachedConnectors(text: string): {
  connectors: string[];
  remainingText: string;
} {
  return {
    connectors: attachedConnectorNames(text),
    remainingText: stripAttachedConnectorsBlock(text),
  };
}

function ConnectorContextPills({ names }: { names: readonly string[] }) {
  const [tenantConnectors] = useQuery(queries.connectorsIndex());
  const identifierByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of tenantConnectors) map.set(c.name, c.identifier);
    return map;
  }, [tenantConnectors]);
  return (
    <>
      {names.map((name) => (
        <CodeContextLabel key={`connector:${name}`}>
          <span style={{ display: "flex", flexShrink: 0 }}>
            <ConnectorLogo
              template={{
                name,
                identifier: connectorPillIdentifier(name, identifierByName),
              }}
              size={10}
            />
          </span>
          <Text variant="text-small-md" ellipsis>
            {name}
          </Text>
        </CodeContextLabel>
      ))}
    </>
  );
}

export function MessageContent({
  text,
  isDictated,
  lineClamp,
  actions,
  hideVoice,
  leftBadge,
}: {
  text: string;
  isDictated: boolean;
  lineClamp?: number;
  actions?: React.ReactNode;
  hideVoice?: boolean;
  leftBadge?: React.ReactNode;
}) {
  const { context: monitoringCtx, remainingText: afterMonitoring } =
    extractChatInvestigation(text);
  const { snippets: codeCtx, remainingText: afterCode } =
    parseCodeContext(afterMonitoring);
  const { snippets: elemCtx, remainingText: afterElem } =
    parseElementContext(afterCode);
  const { skills: skillCtx, remainingText: afterSkills } =
    parseAttachedSkills(afterElem);
  const { steps: stepCtx, remainingText: afterSteps } =
    parseAttachedSteps(afterSkills);
  const { connectors: connectorCtx, remainingText } =
    parseAttachedConnectors(afterSteps);
  return (
    <>
      {monitoringCtx && (
        <CodeContextLabel>
          <Icon name="scan-heart" size={10} style={{ flexShrink: 0 }} />
          <Text variant="text-small-md" ellipsis>
            {getInvestigationContent(monitoringCtx.surface)?.sourceTitle}
          </Text>
        </CodeContextLabel>
      )}
      {skillCtx.map((name) => (
        <CodeContextLabel key={`skill:${name}`}>
          <Icon name="skills" size={10} style={{ flexShrink: 0 }} />
          <Text variant="text-small-md" ellipsis>
            {name}
          </Text>
        </CodeContextLabel>
      ))}
      {stepCtx.map((name) => (
        <CodeContextLabel key={`step:${name}`}>
          <Icon name="puzzle" size={10} style={{ flexShrink: 0 }} />
          <Text variant="text-small-md" ellipsis>
            {name}
          </Text>
        </CodeContextLabel>
      ))}
      {connectorCtx.length > 0 && (
        <ConnectorContextPills names={connectorCtx} />
      )}
      {codeCtx.map((s) => (
        <CodeContextLabel key={`${s.file}:${s.lines}`}>
          <AccentBar style={{ color: `var(--${s.color}-600)` }} />
          <Text variant="text-small-md" ellipsis>
            {s.file}: {s.lines}
          </Text>
        </CodeContextLabel>
      ))}
      {elemCtx.map((s) => (
        <CodeContextLabel key={`${s.step}:${s.element}`}>
          <Icon
            name="mouse-pointer-2"
            size={10}
            color={`var(--${s.color}-600)`}
            style={{ flexShrink: 0 }}
          />
          <Text variant="text-small-md" ellipsis>
            {s.index}
            <MetaSep />
            {s.element}
          </Text>
        </CodeContextLabel>
      ))}
      {remainingText && (
        <Text variant="text-regular">
          <span
            style={{
              whiteSpace: "pre-wrap",
              ...(isDictated && { fontStyle: "italic" }),
              ...(lineClamp != null && {
                display: "-webkit-box",
                WebkitLineClamp: lineClamp,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
            }}
          >
            {isDictated ? `"${remainingText}"` : remainingText}
          </span>
        </Text>
      )}
      {(actions || (isDictated && !hideVoice) || leftBadge) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minHeight: 12,
          }}
        >
          {leftBadge}
          {(actions || (isDictated && !hideVoice)) && (
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {actions}
              {isDictated && !hideVoice && (
                <VoiceLabel style={{ alignSelf: "center" }}>
                  <Icon name="audio-lines" size={10} />
                  <Text variant="text-small-md">Voice</Text>
                </VoiceLabel>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
